import { IParsedTable } from '../parse-csv';
import { parseJsonAssetTypes } from './parse-json-asset-types';
import { readFilePrefix } from './prefix-sample';

/**
 * Converts a JSON import into the CSV the server knows how to read. [AVP-891]
 *
 * JSON never reaches the server. `parseJsonAssetTypes` builds its header from
 * the union of keys across the rows it actually sees, so a 50-row sample in
 * the browser and a 200 000-row pass on the server would disagree about the
 * columns — and a column mapping carried by index cannot survive that. Rather
 * than maintain a Java twin of its four accepted shapes and three property
 * encodings, the browser reads the JSON whole, runs the parser it already has,
 * and uploads an RFC4180 CSV instead. One parser on the server, and the
 * existing JSON test suite keeps its meaning.
 *
 * The cost is that JSON must fit in memory, which is why it is capped well
 * below the CSV limit.
 */

const needsQuotes = (value: string): boolean => (
  value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')
);

export const toCsvCell = (value: string): string => {
  if (value === '') {
    return '';
  }
  return needsQuotes(value) ? `"${value.replace(/"/g, '""')}"` : value;
};

export const toCsvText = (table: IParsedTable): string => {
  const lines = [table.header.map(toCsvCell).join(',')];
  table.rows.forEach((row) => lines.push(row.map(toCsvCell).join(',')));
  // trailing newline: the parser ignores it, and most editors expect it
  return `${lines.join('\r\n')}\r\n`;
};

export interface IJsonConversion {
  blob: Blob;
  table: IParsedTable;
}

/**
 * @throws Error when the file has no readable rows
 */
export const jsonFileToCsv = async (file: Blob): Promise<IJsonConversion> => {
  // FileReader, not Blob.text(): jsdom 16.7 (react-scripts 4) has no .text()
  const text = await readFilePrefix(file, file.size);
  // no row cap: whatever the file holds has to reach the server, and the
  // caller has already refused anything over MAX_JSON_BYTES
  const table = parseJsonAssetTypes(text, { maxRows: Number.MAX_SAFE_INTEGER });

  if (table.header.length === 0) {
    throw new Error(table.errors[0]?.message ?? 'The file has no readable rows.');
  }

  return {
    blob: new Blob([toCsvText(table)], { type: 'text/csv;charset=utf-8;' }),
    table,
  };
};

export default jsonFileToCsv;
